// Nó do flowchart como <div> absoluto posicionado em coords de mundo.
// Reaproveita o visual do antigo NodeCard. Três variantes: team | agent | tool.

import { normalizeTools, type FlowNodeModel } from './layout';
import type { TeamSummary, AgentSummary, ToolSummary } from '../../types/agno-api';

function modelOf(payload: TeamSummary | AgentSummary): string | undefined {
  return payload.model?.model || payload.model?.name;
}

// Cor de realce do nó: supervisor usa primary; agente/tool usam sua chart-N.
function accentColor(node: FlowNodeModel): string {
  if (node.kind === 'team') return 'var(--color-primary)';
  return `var(--color-chart-${node.colorIndex ?? 1})`;
}

// Elevação tokenizada por estado (sem sombra cinza genérica do Tailwind).
// - selecionado/arrastando: anel na cor do nó + leve glow na mesma cor;
// - repouso: anel fino no border;
// O anel de contraste (color-card) separa o realce do fundo em light e dark.
function nodeShadow(node: FlowNodeModel, active: boolean): string {
  const c = accentColor(node);
  if (active) {
    return [
      `0 0 0 1px var(--color-card)`,
      `0 0 0 3px color-mix(in oklab, ${c} 55%, transparent)`,
      `0 4px 14px -4px color-mix(in oklab, ${c} 45%, transparent)`,
    ].join(', ');
  }
  return `0 0 0 1px color-mix(in oklab, var(--color-border) 80%, transparent)`;
}

export function FlowNode({
  node,
  pos,
  selected,
  dragging,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onToggleTools,
}: {
  node: FlowNodeModel;
  pos: { x: number; y: number };
  selected: boolean;
  dragging: boolean;
  onPointerDown: (e: PointerEvent) => void;
  onPointerMove: (e: PointerEvent) => void;
  onPointerUp: (e: PointerEvent) => void;
  // Chip-contador de tools (só no agente colapsado) expande só esse agente.
  onToggleTools?: () => void;
}) {
  // Realce ativo = selecionado ou em drag → anel/glow na cor do nó.
  const active = selected || dragging;
  // Sem `border` Tailwind: a moldura vem do box-shadow tokenizado (nodeShadow),
  // o que evita a borda dupla e o hover:shadow cinza "de default".
  const base =
    'absolute select-none rounded-[var(--radius)] bg-card text-left ' +
    'transition-[box-shadow] duration-150 will-change-transform';
  const z = dragging ? 'z-20' : 'z-10';
  const cursor = dragging ? 'cursor-grabbing' : 'cursor-grab';

  const style = {
    left: `${pos.x}px`,
    top: `${pos.y}px`,
    width: `${node.w}px`,
    height: `${node.h}px`,
    boxShadow: nodeShadow(node, active),
  };

  if (node.kind === 'team') {
    const team = node.payload as TeamSummary;
    const model = modelOf(team);
    const members = team.members?.length ?? 0;
    const tools = normalizeTools(team.tools).length;
    // Descrição do supervisor (pode ser frase longa) → clamp 2 linhas + title.
    const desc = team.description?.trim();
    return (
      <button
        type="button"
        role="button"
        tabIndex={0}
        class={`${base} ${z} ${cursor} overflow-hidden p-3`}
        style={style}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
      >
        <Handle side="right" node={node} />
        <div class="flex items-start gap-2.5">
          <div class="size-8 rounded-md flex items-center justify-center shrink-0 bg-primary text-primary-foreground">
            <i class="ph ph-tree-structure text-base" />
          </div>
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2">
              <span class="font-sans font-medium text-sm truncate">{team.name || team.id}</span>
              <span class="text-[10px] text-muted-foreground border border-border rounded px-1 py-0.5 font-mono shrink-0">
                supervisor
              </span>
            </div>
            {desc && (
              <p
                class="mt-1 text-[11px] leading-snug text-muted-foreground line-clamp-2"
                title={desc}
              >
                {desc}
              </p>
            )}
            <div class="flex items-center gap-2.5 mt-1.5 text-[10px] text-muted-foreground font-mono">
              {model && (
                <span class="inline-flex items-center gap-1 min-w-0 truncate">
                  <i class="ph ph-cpu text-xs shrink-0" /> {model}
                </span>
              )}
              <span class="inline-flex items-center gap-1 shrink-0">
                <i class="ph ph-users text-xs" /> {members}
              </span>
              <span class="inline-flex items-center gap-1 shrink-0">
                <i class="ph ph-wrench text-xs" /> {tools}
              </span>
            </div>
          </div>
        </div>
      </button>
    );
  }

  if (node.kind === 'agent') {
    const agent = node.payload as AgentSummary;
    const model = modelOf(agent);
    const toolCount = normalizeTools(agent.tools).length;
    const c = `var(--color-chart-${node.colorIndex ?? 1})`;
    // Intenção do subagente: role (frase) com fallback para description.
    // Renderizada como bloco de até 2 linhas (line-clamp-2) + title no hover —
    // nunca mais como tag inline shrink-0 que transbordava a caixinha.
    const intent = (agent.role || agent.description)?.trim();
    return (
      <button
        type="button"
        role="button"
        tabIndex={0}
        class={`${base} ${z} ${cursor} overflow-hidden border-l-[3px] p-3`}
        style={{ ...style, borderLeftColor: c }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
      >
        <Handle side="left" node={node} />
        <Handle side="right" node={node} />
        <div class="flex items-start gap-2.5">
          <div
            class="size-8 rounded-md flex items-center justify-center shrink-0"
            style={{ backgroundColor: c, color: 'var(--color-primary-foreground)' }}
          >
            <i class="ph ph-users text-base" />
          </div>
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2">
              <span class="font-sans font-medium text-sm truncate">{agent.name || agent.id}</span>
              <span class="text-[10px] text-muted-foreground border border-border rounded px-1 py-0.5 font-mono shrink-0">
                agent
              </span>
            </div>
            {intent && (
              <p
                class="mt-1 text-[11px] leading-snug text-muted-foreground line-clamp-2"
                title={intent}
              >
                {intent}
              </p>
            )}
            <div class="flex items-center gap-2.5 mt-1.5 text-[10px] text-muted-foreground font-mono">
              {model && (
                <span class="inline-flex items-center gap-1 min-w-0 truncate">
                  <i class="ph ph-cpu text-xs shrink-0" /> {model}
                </span>
              )}
              {toolCount > 0 && (
                <span
                  class="inline-flex items-center gap-1 shrink-0 rounded border border-border px-1 py-0.5 hover:bg-accent/40"
                  role="button"
                  tabIndex={0}
                  title="Expandir/recolher tools deste agente"
                  onPointerDown={(e) => {
                    e.stopPropagation();
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleTools?.();
                  }}
                >
                  <i class="ph ph-wrench text-xs" /> {toolCount} {toolCount === 1 ? 'tool' : 'tools'}
                </span>
              )}
            </div>
          </div>
        </div>
      </button>
    );
  }

  // tool
  const tool = node.payload as ToolSummary;
  const c = `var(--color-chart-${node.colorIndex ?? 1})`;
  return (
    <button
      type="button"
      role="button"
      tabIndex={0}
      class={`${base} ${z} ${cursor} flex items-center gap-2 border px-2.5 bg-muted`}
      style={{ ...style, borderStyle: 'dashed', borderColor: c }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
    >
      <Handle side="left" node={node} />
      <i class="ph ph-wrench text-xs shrink-0 text-muted-foreground" />
      <span class="font-mono text-[11px] truncate">{tool.name}</span>
    </button>
  );
}

// Handle visual (ponto na borda). Posicionado no centro-vertical da borda lateral.
// Cor do nó (chart-N para agente/tool, primary para supervisor) + anel de
// contraste (color-card) que o destaca sobre o grid em light e dark.
// size-3 = 12px → centro fica a -6px da borda lateral.
function Handle({ side, node }: { side: 'left' | 'right'; node: FlowNodeModel }) {
  const off = `${node.h / 2 - 6}px`;
  const style = {
    ...(side === 'right' ? { right: '-6px', top: off } : { left: '-6px', top: off }),
    backgroundColor: accentColor(node),
    boxShadow: '0 0 0 2px var(--color-card)',
  };
  return (
    <span
      class="absolute size-3 rounded-full pointer-events-none"
      style={style}
      aria-hidden="true"
    />
  );
}
